import "../lib/bootstrap-css";
import React, { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import Pagination from "./pagination";
import {
	INVENTORY_BRANDS,
	INVENTORY_OVERVIEW,
	getBrandBySlug,
	getBrandHref,
	getInventoryShared,
} from "./inventory-page-data";
import SeoJsonLd from "./jsonld/seo-jsonld";

export default function InventoryBrandParts({ locale = "es", slug, db, pageSize }) {
	const brand =
		getBrandBySlug(locale, slug) ||
		INVENTORY_BRANDS.find((b) => b.esSlug === slug || b.enSlug === slug);
	const copy = brand?.[locale] || brand?.en;
	const shared = getInventoryShared(locale);
	const overview = INVENTORY_OVERVIEW[locale] || INVENTORY_OVERVIEW.en;
	const ogLocale = locale === "es" ? "es_ES" : "en";
	const contactHref = overview.contactHref;

	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");

	const filteredDb = useMemo(() => {
		if (!db) return [];
		const term = searchTerm.trim().toLowerCase();
		if (!term) return db;
		return db.filter(
			(val) =>
				val.Description?.toLowerCase().includes(term) ||
				val.Reference?.toLowerCase().includes(term) ||
				val.Replacements?.toLowerCase().includes(term)
		);
	}, [db, searchTerm]);

	const currentRows = useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		return filteredDb.slice(start, start + pageSize);
	}, [currentPage, pageSize, filteredDb]);

	const totalPages = Math.max(1, Math.ceil(filteredDb.length / pageSize));

	useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm]);

	useEffect(() => {
		setCurrentPage((prev) => Math.min(Math.max(1, prev), totalPages));
	}, [totalPages]);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [currentPage]);

	if (!brand || !copy) {
		return null;
	}

	return (
		<section className="tu-inventory tu-inventory--brand" lang={locale}>
			<Head>
				<meta charSet="UTF-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<link rel="shortcut icon" type="image/png" href="/images/tu-favicon.png" />
				<meta
					name="facebook-domain-verification"
					content="fzctnjbrtlybvytmamk8glkng9af7p"
				/>
				<title>{copy.pageTitle}</title>
				<meta name="description" content={copy.metaDescription} />
				<meta name="keywords" content={copy.metaKeywords} />
				<meta name="author" content="Edgar Lindo" />
				<meta property="og:locale" content={ogLocale} />
				<meta property="site_name" content="TU" />
				<meta property="og:title" content={copy.ogTitle} />
				<meta property="og:description" content={copy.ogDescription} />
				<meta property="og:image" content={copy.ogImage} />
				<meta property="og:url" content={copy.ogUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:creator" content="@MrEdgarLindo" />
				<meta name="twitter:title" content={copy.twitterTitle} />
				<meta name="twitter:description" content={copy.twitterDescription} />
				<meta name="twitter:image" content={copy.ogImage} />
			</Head>

			<div className="tu-container">
				<SeoJsonLd
					locale={locale}
					pageId={`inventory-${slug}`}
					breadcrumbClassName="tu-inventory__breadcrumb"
				/>

				<header className="tu-inventory__header">
					<h1 className="tu-inventory__title">{copy.heading}</h1>
					<p className="tu-inventory__intro">
						{copy.introLead} {copy.introMiddle}{" "}
						<Link href={contactHref}>{shared.contactUs}</Link> {copy.introTrail}
					</p>
				</header>

				<div className="tu-inventory__toolbar">
					<label className="tu-inventory__search-wrap">
						<span className="visually-hidden">{shared.searchTitle}</span>
						<input
							type="search"
							className="tu-inventory__search form-control"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder={shared.searchPlaceholder}
							aria-label={shared.searchTitle}
						/>
					</label>
					<p className="tu-inventory__count" aria-live="polite">
						{shared.resultsLabel(filteredDb.length)}
					</p>
				</div>

				{currentRows.length === 0 ? (
					<p className="tu-inventory__empty">{shared.emptyResults}</p>
				) : (
					<div className="tu-inventory__table-wrap">
						<table className="tu-inventory__table table">
							<thead>
								<tr>
									<th scope="col">{shared.tableReference}</th>
									<th scope="col">{shared.tableDescription}</th>
									<th scope="col">{shared.tableReplacement}</th>
								</tr>
							</thead>
							<tbody>
								{currentRows.map((item, index) => (
									<tr key={`${item.Reference}-${index}`}>
										<td data-label={shared.tableReference}>{item.Reference}</td>
										<td data-label={shared.tableDescription}>
											{item.Description}
										</td>
										<td data-label={shared.tableReplacement}>
											{item.Replacements}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{filteredDb.length > pageSize ? (
					<div className="tu-inventory__pagination">
						<Pagination
							className="pagination-bar"
							currentPage={currentPage}
							totalCount={filteredDb.length}
							pageSize={pageSize}
							onPageChange={setCurrentPage}
						/>
					</div>
				) : null}

				<aside className="tu-inventory__brand-nav" aria-label={overview.brandsHeading}>
					<p className="tu-inventory__brand-nav-title">{overview.brandsHeading}</p>
					<ul>
						{INVENTORY_BRANDS.map((b) => {
							const href = getBrandHref(b, locale);
							const active = b.key === brand.key;
							return (
								<li key={b.key}>
									<Link
										href={href}
										className={active ? "is-active" : undefined}
										aria-current={active ? "page" : undefined}
									>
										{b.label}
									</Link>
								</li>
							);
						})}
					</ul>
				</aside>
			</div>
		</section>
	);
}
